/**
 * MAP CONTRACT — the engine's public surfaces. Owned by the main loop.
 *
 *   MapController   (B2, src/map/controller.ts)   map lifecycle, style/moods, camera primitives
 *   LightLayerAPI   (B3, src/map/gl/index.ts)      custom WebGL route/outline ribbons + glow + comets
 *   MarkersAPI      (B5, src/map/markers/index.ts) dots, clusters, labels, glass pins, site mark, ground pulses
 *   OverlaysAPI     (B5, src/map/layers/index.ts)  site polygon, corridors (roads/future), distance rings
 *   Choreographer   (B4, src/map/camera/choreo.ts) every sequence: select, bloom, preview, drive, intro, home
 *
 * Engine rule: React never renders per frame. Per-frame values go to GPU
 * uniforms, MapLibre paint props or DOM refs, driven by src/lib/ticker.ts.
 *
 * Data rule: the map is created only after preload.essential resolves, so
 * appStore.getState().{data, routes, geo} are ALWAYS populated when any factory
 * or the controller runs. Factories may read them at creation (e.g. overlays
 * read geo.site/roads/future/contours; the controller reads geo.contours).
 * The Choreographer, on creation, pushes route + site-outline paths into
 * light().setPaths() and places into markers().setPlaces().
 *
 * Visual spec for everything here: docs/design-spec.md (§D map rendering,
 * §E motion) and src/map/style/moods.ts (MOODS: every colour).
 */
import type { Map as MLMap } from "maplibre-gl";
import type { Category, LngLat, Place } from "./data";
import type { Insets, LayerFlags, Mood, SelectSource } from "./store";

/* ─── Ids and z-order ────────────────────────────────────────────────────── */

export const SOURCE_IDS = {
  basemap: "basemap", //       pmtiles://basemap (vector, Protomaps schema v4)
  dem: "dem", //               raster-dem via ldem://dem/{z}/{x}/{y} (B1 handler; terrarium, 512; synthesises missing z10–12 tiles)
  contours: "ld-contours",
  contoursLit: "ld-contours-lit", // lit contour segments around the destination (spec §D.10)
  site: "ld-site",
  roads: "ld-roads",
  future: "ld-future",
  rings: "ld-rings",
  places: "ld-places", //      GeoJSON points (clustered variant: ld-places-cluster)
  placesCluster: "ld-places-cluster",
  pinProxy: "ld-pin-proxy",
  fallbackRoute: "ld-sel", //  line-gradient fallback (lineMetrics: true)
  anchors: "ld-empty",
} as const;

/** Empty slot layers created by the controller right after the basemap style.
 *  Others insert with map.addLayer(layer, ANCHORS.x) — i.e. just beneath the
 *  anchor, stacking in insertion order.
 *    basemap earth/water/landuse
 *    ── terrain   → hillshade, contours
 *    basemap roads, bridges
 *    ── overlays  → site fill/outline, rings, roads highlight, future corridors
 *    ── routes    → LightLayer (custom) + fallback route layers
 *    basemap labels (symbol)
 *    ── places    → place dots, clusters, labels, ring labels
 *    ── top       → pin proxies (topmost symbols so collisions route around DOM pins)
 */
export const ANCHORS = {
  terrain: "ld-anchor-terrain",
  overlays: "ld-anchor-overlays",
  routes: "ld-anchor-routes",
  places: "ld-anchor-places",
  top: "ld-anchor-top",
} as const;

export const LAYER_IDS = {
  hillshade: "ld-hillshade",
  contoursMinor: "ld-contours-minor",
  contoursMajor: "ld-contours-major",
  contoursLabel: "ld-contours-label",
  ridge: "ld-ridge",
  veil: "ld-veil", //              background layer at ANCHORS.overlays: the land recedes on select (spec §D.10)
  contoursLit: "ld-contours-lit", // directly above the veil
  siteFill: "ld-site-fill",
  siteLine: "ld-site-line",
  rings: "ld-rings",
  ringsLabel: "ld-rings-label",
  roadsCase: "ld-roads-case",
  roadsLine: "ld-roads-line",
  futureLine: "ld-future-line",
  futureLabel: "ld-future-label",
  light: "ld-light", //           CustomLayerInterface
  fallbackGlow: "ld-sel-glow",
  fallbackCore: "ld-sel-core",
  placesDot: "ld-places-dot",
  placesCluster: "ld-places-cluster",
  placesClusterRing1: "ld-places-cluster-ring1", // tree-ring outlines (spec §D.2)
  placesClusterRing2: "ld-places-cluster-ring2",
  placesClusterCount: "ld-places-cluster-count",
  placesLabel: "ld-places-label",
  pinProxy: "ld-pin-proxy",
} as const;

/* ─── Camera ─────────────────────────────────────────────────────────────── */

export type EaseName = "settle" | "drift" | "leave" | "breathe" | "linear";
export type MoveResult = "done" | "interrupted" | "cancelled";

export interface CameraTarget {
  center: LngLat;
  zoom: number;
  bearing?: number;
  pitch?: number;
  padding?: Partial<Insets>;
}

export interface MoveOpts {
  duration?: number; //  ms; omitted = tokens.motion.duration.scene
  ease?: EaseName; //    default "drift"
  signal?: AbortSignal; // aborting resolves "cancelled" (camera stops where it is)
}

export interface MapController {
  readonly ready: Promise<void>; // style + anchors + light/markers/overlays attached
  map(): MLMap;
  light(): LightLayerAPI;
  markers(): MarkersAPI;
  overlays(): OverlaysAPI;

  setMood(m: Mood, o?: { duration?: number }): void; // paint transitions (default 1100 ms)
  getMood(): Mood;
  setLayerVisible(k: keyof LayerFlags, v: boolean): void;

  jumpTo(t: CameraTarget): void;
  easeTo(t: CameraTarget, o?: MoveOpts): Promise<MoveResult>;
  flyTo(t: CameraTarget, o?: MoveOpts & { curve?: number }): Promise<MoveResult>; // van Wijk, curve default 1.42
  stop(): void;
  homeTarget(): CameraTarget; //   site-centred overview for the current layout/insets
  regionTarget(): CameraTarget; // Navi Mumbai → Purandar overview (intro start)

  getInsets(): Insets; //          safe insets (panels) used as camera padding
  setInsets(i: Insets): void;
  /** Fires on user-initiated camera input (drag, wheel, pinch, keyboard pan). */
  onInteract(cb: () => void): () => void;
  isMoving(): boolean;
  destroy(): void;
}

/* ─── LightLayer (custom WebGL) ──────────────────────────────────────────── */

export interface LightPath {
  id: string; //       route id (= place id) | "site-outline" | "ring-5" …
  coords: LngLat[];
  cum: number[]; //    cumulative metres
  lengthM: number;
  kind: "route" | "site" | "ring";
  closed?: boolean;
}

export interface RouteVisualState {
  head: number; //   metres from start that are drawn up to (0 … lengthM)
  tail: number; //   metres from start that are hidden behind (0 = from the origin); retract = tail→head
  alpha: number; //  0…1 overall
  glow: number; //   0…1 glow strength
  width: number; //  core width in CSS px
  pulse: number; //  centre of the travelling light packet in metres, or -1 for none
  pulseSigma: number; // packet half-width in metres
  comet: boolean; // draw the sun comet head at `head`
  style: "hero" | "bloom" | "preview" | "site" | "ring";
}

export interface LightPalette {
  core: string; //   hex — the lit line
  trail: string; //  hex — colour far behind the head
  glow: string; //   hex
  comet: string; //  hex — comet core
  halo: string; //   hex — comet halo
  bloom: string; //  hex — bloom ridge lines
  preview: string; // hex — hover preview
}

export interface LightLayerAPI {
  readonly id: string; //            LAYER_IDS.light
  readonly mode: "gl" | "fallback";
  setPaths(paths: LightPath[]): void; // (re)builds GPU buffers; call once with all routes + site outline
  setState(id: string, s: Partial<RouteVisualState>): void; // cheap; batched into the state texture next frame
  getState(id: string): RouteVisualState | undefined;
  setPalette(p: LightPalette, o?: { duration?: number }): void; // tween (default 1100 drift; 2400 for the intro sunrise)
  /** Point + travel bearing at `m` metres along a path (binary search on cum). */
  pointAt(id: string, m: number): { lngLat: LngLat; bearing: number } | null;
  requestRender(): void;
  onModeChange(cb: (mode: "gl" | "fallback") => void): () => void;
  destroy(): void;
}

/* ─── Markers + overlays ─────────────────────────────────────────────────── */

export interface MarkersAPI {
  setPlaces(places: Place[], categories: Category[]): void;
  /** Places shown as glass pins (active category / selection). null = zoom rules only. */
  setFocusSet(ids: string[] | null): void;
  setSelected(id: string | null): void;
  setHovered(id: string | null): void;
  setDimmed(on: boolean): void; //          dim non-focus dots while something is selected
  /** Bloom arrival: pin scales in + one ground ring. */
  arrive(id: string, o?: { ring?: boolean }): void;
  /** Tree-ring pulse on the ground at a coordinate (pitch-aligned). */
  pulse(at: LngLat, o?: { count?: number; staggerMs?: number; radiusPx?: number; durationMs?: number }): void;
  /** The Whorl site mark. progress drives the ring draw (intro). */
  setSite(o: { visible?: boolean; progress?: number; label?: boolean }): void;
  setMood(m: Mood, o?: { duration?: number }): void;
  destroy(): void;
}

export interface OverlaysAPI {
  setBoundary(o: { visible: boolean; fill?: number }): void;
  /** Distance rings. animate = ripple out (stagger), resolves when settled. */
  setRings(o: { visible: boolean; animate?: boolean }): Promise<void>;
  /** Existing corridors. highlight = a CorridorId to light up (road places). */
  setRoads(o: { visible: boolean; highlight?: string | null; bloom?: string[] | null }): void; // bloom: corridors lit at bloom strength (arterial-roads bloom)
  setFuture(o: { visible: boolean; highlight?: string | null; bloom?: boolean }): void; //      bloom: every future corridor at full strength (future-infrastructure bloom)
  /** The veil (spec §D.10): background layer opacity 0..1, tweened on the ticker. */
  setVeil(v: number, o?: { duration?: number; ease?: EaseName }): void;
  /** "The land answers": light the contour segments within radiusPx of `at` at the arrival zoom,
   *  rise 0→1 over durationMs (default 1600 settle) then settle to `rest` (default 0.6, 2400 breathe).
   *  null fades them out (294 leave). */
  lightContours(at: LngLat | null, o?: { zoom: number; radiusPx?: number; rest?: number; durationMs?: number; delayMs?: number; ease?: EaseName }): void;
  setMood(m: Mood, o?: { duration?: number }): void;
  destroy(): void;
}

/* ─── Choreographer ──────────────────────────────────────────────────────── */

export interface Choreographer {
  /** The flagship: frame → draw with comet → count-up → tree rings → idle pulse. */
  select(id: string, o?: { source?: SelectSource; signal?: AbortSignal }): Promise<void>;
  /** Retract the current route (0.7×) and return home. */
  clear(o?: { home?: boolean }): Promise<void>;
  /** Draw every route of a sub-category, nearest first. */
  bloom(categoryId: string): Promise<void>;
  clearBloom(): Promise<void>;
  /** Hover: faint route preview + marker light arriving. null clears. */
  preview(id: string | null): void;
  /** Follow-cam ride along the selected route. */
  drive(id: string, o?: { signal?: AbortSignal }): Promise<"done" | "interrupted" | "cancelled">;
  /** Map beats of the intro (emits "intro:beat"). mode "short" = 420 ms cross-fade to final state. */
  intro(o?: { mode?: "full" | "short"; signal?: AbortSignal }): Promise<void>;
  home(): Promise<void>;
  cancel(): void;
  destroy(): void;
}

/* ─── Moods (palette data, S4 → src/map/style/moods.ts exports MOODS) ───── */

/** One map mood. Every colour the map engine paints comes from here (hex or
 *  rgba strings). Consumers: B2 (basemap flavor, hillshade, contours), B3
 *  (light palette), B5 (dots, labels, rings, corridors, site). */
export interface MoodSpec {
  key: Mood;
  label: string; //           "Golden hour"
  clock: string; //           "6:05 pm" (LightIsland readout)
  /** @protomaps/basemaps Flavor. Sprite-dependent layers are dropped by B2. */
  flavor: import("@protomaps/basemaps").Flavor;
  light: LightPalette;
  contours: { minor: string; major: string; label: string; halo: string; ridge: string; minorWidth: number; majorWidth: number };
  hillshade: { shadow: string; highlight: string; accent: string; exaggeration: number; illuminationDirection: number };
  overlays: {
    ring: string; ringLabel: string; ringLabelHalo: string;
    siteFill: string; siteFillOpacity: number; siteLine: string;
    road: string; roadCase: string; roadHighlight: string;
    future: string; futureLabel: string; futureLabelHalo: string;
  };
  places: {
    dot: Record<"A" | "B" | "C" | "D", string>; // group colours (tokens only: sun, sand, sage, sky, terracotta …)
    dotStroke: string;
    label: string; labelHalo: string;
    cluster: string; clusterStroke: string; clusterText: string;
  };
  /** CSS custom properties applied on the stage root for DOM overlays (glass pins, route meter). */
  cssVars: Record<string, string>;
}

/* ─── Factories (signatures the integrator relies on) ───────────────────── */

export interface EngineData {
  places: Place[];
  categories: Category[];
}

export type CreateLightLayer = (map: MLMap, o: { beforeId: string; reducedMotion: () => boolean }) => LightLayerAPI;
export type CreateMarkers = (map: MLMap, o: { beforeId: string; topId: string; reducedMotion: () => boolean }) => MarkersAPI;
export type CreateOverlays = (map: MLMap, o: { beforeId: string; placesBeforeId: string; reducedMotion: () => boolean }) => OverlaysAPI;
